import React from "react";
import {
	Button,
	ButtonProps,
	ChildrenProps,
	Column,
	DefaultContextValue,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	Section,
	useRequiredContext,
} from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import { InternalLink } from "@hex-insights/router";
import { Icon } from "../../../../Components";
import { permissions, UserPublicNameFormat, useViewerPublicNameFormatQuery } from "../../../../Utilities";
import {
	AnnouncementCreateModalAnimatedIconController,
	AnnouncementCreatePermissionRequired,
} from "../../Announcement";
import {
	CalendarEventCreateModalAnimatedIconController,
	CalendarEventCreatePermissionRequired,
} from "../../CalendarEvent";
import { NewsletterCreateModalAnimatedIconController, NewsletterCreatePermissionRequired } from "../../Newsletter";
import { PostCreateModalAnimatedIconController, PostCreatePermissionRequired } from "../../Post";
import styles from "./styles.module.css";

const communicationCreatePermissions = [
	permissions.Announcement.Create.Admin,
	permissions.Announcement.Create.Basic,
	permissions.Newsletter.Create.Admin,
	permissions.Newsletter.Create.Basic,
	permissions.Post.Create.Admin,
	permissions.Post.Create.Basic,
	permissions.CalendarEvent.Create.Admin,
	permissions.CalendarEvent.Create.Basic,
];

const createPermissions = [...communicationCreatePermissions];

export function OmniCreateButtonPermissionRequired({ children }: Partial<ChildrenProps>) {
	return (
		<PermissionRequired requiredPermissions={createPermissions} checkMethod="or">
			{children}
		</PermissionRequired>
	);
}

export type OmniCreateModalContextValue = {
	isModalOpen: boolean;
	openModal: () => void;
	closeModal: () => void;
	preferencesPageTo: string;
};

export const OmniCreateModalContext = React.createContext<OmniCreateModalContextValue>(
	new DefaultContextValue("OmniCreateModalContext") as any,
);

type OmniCreateModalContextManagerProps = {
	preferencesPageTo: string;
} & Partial<ChildrenProps>;

export function OmniCreateModalContextManager({ preferencesPageTo, children }: OmniCreateModalContextManagerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const contextValue = React.useMemo(
		() => ({
			isModalOpen,
			openModal,
			closeModal,
			preferencesPageTo,
		}),
		[isModalOpen, openModal, closeModal, preferencesPageTo],
	);

	return (
		<OmniCreateModalContext.Provider value={contextValue}>
			{children}

			<Modal.If condition={isModalOpen}>
				<OmniCreateModal onClose={closeModal} />
			</Modal.If>
		</OmniCreateModalContext.Provider>
	);
}

export type OmniCreateButtonProps = Omit<ButtonProps, "onClick">;

export function OmniCreateButton({
	variant = "primary",
	children = (
		<Row justify="spaced-center" align="center" horizontalSpacing="0.375rem">
			<Icon.Plus size={16} /> Create
		</Row>
	),
	...props
}: OmniCreateButtonProps) {
	const { openModal } = useRequiredContext(OmniCreateModalContext);

	return (
		<Button variant={variant} onClick={openModal} {...props}>
			{children}
		</Button>
	);
}

const communicationGridResponsiveTracks = {
	0: 1,
	400: 2,
};

type OmniCreateModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function OmniCreateModal({ ifRef, onClose, className, style }: OmniCreateModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	const { data: viewerData } = useViewerPublicNameFormatQuery();
	const isNameHidden = viewerData?.viewer?.publicNameFormat === UserPublicNameFormat.Hidden;
	const canCreate = !!viewerData && !isNameHidden;

	const { preferencesPageTo } = useRequiredContext(OmniCreateModalContext);

	return (
		<Modal
			size="large"
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["omni-create-modal"], className)}
			style={style}
		>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="2rem">
					<PermissionRequired requiredPermissions={communicationCreatePermissions} checkMethod="or">
						<Section>
							<Section.Header>
								<Heading.H3 noMargin>Create a Post</Heading.H3>
							</Section.Header>
							<Section.Body>
								<Grid responsiveTracks={communicationGridResponsiveTracks} style={{ alignItems: "center" }} gap="1rem">
									<AnnouncementCreatePermissionRequired>
										<AnnouncementCreateModalAnimatedIconController
											onSuccess={onSuccess}
											disabled={!canCreate}
											style={{ width: "100%" }}
										/>
									</AnnouncementCreatePermissionRequired>
									<NewsletterCreatePermissionRequired>
										<NewsletterCreateModalAnimatedIconController
											onSuccess={onSuccess}
											disabled={!canCreate}
											style={{ width: "100%" }}
										/>
									</NewsletterCreatePermissionRequired>
									<PostCreatePermissionRequired>
										<PostCreateModalAnimatedIconController
											onSuccess={onSuccess}
											disabled={!canCreate}
											style={{ width: "100%" }}
										/>
									</PostCreatePermissionRequired>
									<CalendarEventCreatePermissionRequired>
										<CalendarEventCreateModalAnimatedIconController
											onSuccess={onSuccess}
											disabled={!canCreate}
											style={{ width: "100%" }}
										/>
									</CalendarEventCreatePermissionRequired>
								</Grid>

								<If condition={isNameHidden}>
									<div style={{ color: "var(--neutral-colors--tertiary)", fontSize: "0.875rem", marginTop: "1.25rem" }}>
										You cannot create content with your name hidden. You can change your name display{" "}
										<InternalLink to={preferencesPageTo} onClick={onClose}>
											on the settings page
										</InternalLink>
										.
									</div>
								</If>
							</Section.Body>
						</Section>
					</PermissionRequired>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
